import { ChartSpec, ChartType, CHART_TYPES } from './chartSpec'

/** Result of validating an untrusted `ChartSpec` from the LLM. */
export type ValidateResult = { ok: true; spec: ChartSpec } | { ok: false; error: string }

/** Hard limits to protect render performance. */
export const MAX_SERIES = 12
export const MAX_POINTS_PER_SERIES = 2000

/** Styling hint keys we accept from the untrusted `options` passthrough. */
const ALLOWED_OPTION_KEYS = new Set([
  'colors',
  'legend',
  'legendPosition',
  'dataLabels',
  'format',
  'horizontal',
  'sparkline'
])

const isFiniteNumber = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v)

/** Coerce a value to plain text (never HTML). */
const asText = (v: unknown): string | undefined => (typeof v === 'string' ? v : undefined)

/**
 * Validate and sanitise an untrusted argument object into a safe `ChartSpec`.
 * Never throws. Clamps oversized data and reconciles length mismatches rather
 * than rejecting, so a slightly-off spec still renders.
 */
export function validateChartSpec(args: unknown): ValidateResult {
  if (!args || typeof args !== 'object') {
    return { ok: false, error: 'Chart spec must be an object.' }
  }

  const raw = args as Record<string, unknown>

  // type
  const type = raw.type as ChartType
  if (!CHART_TYPES.includes(type)) {
    return {
      ok: false,
      error: `Unsupported chart type "${String(raw.type)}".`
    }
  }

  // series
  if (!Array.isArray(raw.series) || raw.series.length === 0) {
    return { ok: false, error: 'Chart spec requires a non-empty "series" array.' }
  }

  const clampedSeries = raw.series.slice(0, MAX_SERIES).map((s) => {
    const seriesObj = (s ?? {}) as Record<string, unknown>
    const name = asText(seriesObj.name) ?? ''
    const data = Array.isArray(seriesObj.data)
      ? seriesObj.data.filter(isFiniteNumber).slice(0, MAX_POINTS_PER_SERIES)
      : []
    return { name, data }
  })

  if (clampedSeries.every((s) => s.data.length === 0)) {
    return { ok: false, error: 'Chart series contain no numeric data.' }
  }

  // categories — reconcile length against the longest series
  let categories = Array.isArray(raw.categories)
    ? raw.categories.map((c) => asText(c) ?? '').slice(0, MAX_POINTS_PER_SERIES)
    : undefined

  if (categories) {
    const longest = Math.max(...clampedSeries.map((s) => s.data.length))
    if (categories.length > longest) {
      categories = categories.slice(0, longest)
    } else if (categories.length < longest) {
      categories = [
        ...categories,
        ...Array.from({ length: longest - categories.length }, (_, i) =>
          String(categories!.length + i + 1)
        )
      ]
    }
  }

  // options passthrough — whitelist only
  let options: Record<string, unknown> | undefined
  if (raw.options && typeof raw.options === 'object') {
    const entries = Object.entries(raw.options as Record<string, unknown>).filter(([key]) =>
      ALLOWED_OPTION_KEYS.has(key)
    )
    if (entries.length > 0) options = Object.fromEntries(entries)
  }

  const spec: ChartSpec = {
    type,
    title: asText(raw.title),
    subtitle: asText(raw.subtitle),
    categories,
    series: clampedSeries,
    xAxisLabel: asText(raw.xAxisLabel),
    yAxisLabel: asText(raw.yAxisLabel),
    stacked: typeof raw.stacked === 'boolean' ? raw.stacked : undefined,
    options
  }

  return { ok: true, spec }
}
